'use client'

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocket } from '@/providers/SocketContext'
import { adminUsersKeys } from '@/hooks/admin/useAdminUsers'
import { presenceKeys } from '@/hooks/presence/usePresenceSnapshot'
import type { AdminUser, AdminUserDetail } from '@/services/admin/users'
import type { PresenceChangedEvent } from '@/types/SocketEvents'
import type { UserPresenceEntry } from '@/types/presence'
import { useIdleDetector } from '@/hooks/presence/useIdleDetector'

interface PresenceContextValue {
  isDrawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
}

const PresenceContext = createContext<PresenceContextValue>({
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
})

/**
 * Provider app-level de presença: listener único de `presence_changed` (patch de cache),
 * refetch on reconnect, monta o idle-detector e detém o estado do drawer.
 */
export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const { socket, socketReady } = useSocket()
  const queryClient = useQueryClient()
  const [isDrawerOpen, setDrawerOpen] = useState(false)

  useIdleDetector()

  // Fase da conexão vista por ESTE effect. A 1ª conexão não rebusca (a query acabou de
  // buscar); toda volta depois de uma queda, sim. O `on('connect')` abaixo não cobre a
  // volta: registrado sob a guarda de `socketReady`, o cleanup o remove na queda e o
  // `connect` da volta dispara antes do re-render re-registrar.
  const conexaoRef = useRef<'nunca' | 'pronta' | 'caiu'>('nunca')

  useEffect(() => {
    if (!socket) return
    if (!socketReady) {
      if (conexaoRef.current === 'pronta') conexaoRef.current = 'caiu'
      return
    }

    const onPresenceChanged = (evt: PresenceChangedEvent) => {
      queryClient.setQueryData<AdminUser[]>(adminUsersKeys.lists(), (old) =>
        old?.map((u) =>
          u.db_id === evt.user_id ? { ...u, presence_status: evt.status, last_seen_at: evt.at } : u,
        ),
      )
      queryClient.setQueryData<AdminUserDetail>(adminUsersKeys.detail(evt.user_id), (old) =>
        old ? { ...old, presence_status: evt.status, last_seen_at: evt.at } : old,
      )
      /* Upsert no snapshot de presença. `old === undefined` é NO-OP de propósito:
         criar a lista aqui faria uma entrada única passar por "a resposta do
         back", e o roster afirmaria `null` sobre todo o resto do time. */
      queryClient.setQueryData<UserPresenceEntry[]>(presenceKeys.snapshot, (old) => {
        if (!old) return old
        const entrada = { db_id: evt.user_id, presence_status: evt.status, last_seen_at: evt.at }
        return old.some((e) => e.db_id === evt.user_id)
          ? old.map((e) => (e.db_id === evt.user_id ? entrada : e))
          : [...old, entrada]
      })
    }
    // Ao reconectar, a fonte de verdade re-sincroniza (o board pode ter perdido eventos offline).
    const onReconnect = () => {
      queryClient.invalidateQueries({ queryKey: adminUsersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: presenceKeys.snapshot })
    }
    if (conexaoRef.current === 'caiu') onReconnect()
    conexaoRef.current = 'pronta'

    socket.on('presence_changed', onPresenceChanged)
    socket.on('connect', onReconnect)
    return () => {
      socket.off('presence_changed', onPresenceChanged)
      socket.off('connect', onReconnect)
    }
  }, [socket, socketReady, queryClient])

  return (
    <PresenceContext.Provider
      value={{ isDrawerOpen, openDrawer: () => setDrawerOpen(true), closeDrawer: () => setDrawerOpen(false) }}
    >
      {children}
    </PresenceContext.Provider>
  )
}

export const usePresence = () => useContext(PresenceContext)
